import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  Leaf, Plus, Calendar, CheckCircle, Play, AlertCircle, Eye, Scale,
  Navigation, Bot, Activity, BarChart3
} from 'lucide-react'
import { getLantanaPatches, updateLantanaPatch, getPatchesSnapshot } from '../../services/api'
import PageHeader from '../../components/PageHeader'
import StatCard from '../../components/StatCard'

export default function LantanaDashboard({ ranchId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('clearing') // 'clearing' or 'health'
  const [statusFilter, setStatusFilter] = useState('')

  // Modal state for clearing a patch
  const [clearingModal, setClearingModal] = useState({ open: false, patch: null })
  const [biomassWeight, setBiomassWeight] = useState('')
  const [actualStartDate, setActualStartDate] = useState('')
  const [actualEndDate, setActualEndDate] = useState('')

  // Modal state for viewing a cleared patch
  const [viewModal, setViewModal] = useState({ open: false, patch: null })

  // Fetch all vegetation patches (defaults to all types)
  const { data: patches, isLoading, error } = useQuery({
    queryKey: ['lantanaPatches', ranchId, statusFilter],
    queryFn: () => getLantanaPatches(ranchId, {
      ...(statusFilter ? { status: statusFilter } : {}),
      vegetation_type: 'lantana',
    }),
  })

  // Fetch vegetation health snapshot (all patch types)
  const { data: healthSnapshot, isLoading: healthLoading } = useQuery({
    queryKey: ['patchesSnapshot', ranchId],
    queryFn: () => getPatchesSnapshot(ranchId),
    enabled: activeTab === 'health',
  })

  // Mark as cleared mutation
  const clearMutation = useMutation({
    mutationFn: ({ patchId, biomassKg, startDate, endDate }) => updateLantanaPatch(patchId, {
      actual_start_date: startDate || null,
      actual_end_date: endDate || new Date().toISOString().split('T')[0],
      estimated_biomass_kg: biomassKg ? parseFloat(biomassKg) : null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['lantanaPatches'])
      setClearingModal({ open: false, patch: null })
      setBiomassWeight('')
      setActualStartDate('')
      setActualEndDate('')
    },
  })

  // Start clearing mutation
  const startClearMutation = useMutation({
    mutationFn: (patchId) => updateLantanaPatch(patchId, {
      actual_start_date: new Date().toISOString().split('T')[0],
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['lantanaPatches'])
    },
  })

  // Calculate summary stats
  const stats = {
    total: patches?.length || 0,
    identified: patches?.filter(p => p.status === 'identified').length || 0,
    scheduled: patches?.filter(p => p.status === 'scheduled').length || 0,
    inProgress: patches?.filter(p => p.status === 'clearing_in_progress').length || 0,
    cleared: patches?.filter(p => p.status === 'cleared').length || 0,
  }

  const getStatusBadge = (status) => {
    const badges = {
      identified: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Identified' },
      scheduled: { color: 'bg-blue-100 text-blue-800', icon: Calendar, label: 'Scheduled' },
      clearing_in_progress: { color: 'bg-purple-100 text-purple-800', icon: Play, label: 'In Progress' },
      cleared: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Cleared' },
    }
    return badges[status] || badges.identified
  }

  const getDensityBadge = (density) => {
    const badges = {
      sparse: { color: 'bg-green-50 text-green-700', label: 'Sparse' },
      moderate: { color: 'bg-yellow-50 text-yellow-700', label: 'Moderate' },
      dense: { color: 'bg-red-50 text-red-700', label: 'Dense' },
    }
    return badges[density] || { color: 'bg-gray-50 text-gray-700', label: density || 'Unknown' }
  }

  const getNdviColor = (ndvi) => {
    if (ndvi === null || ndvi === undefined) return 'text-gray-400'
    const val = parseFloat(ndvi)
    if (val < 0.2) return 'text-red-600'
    if (val < 0.4) return 'text-yellow-600'
    if (val < 0.6) return 'text-green-600'
    return 'text-emerald-700'
  }

  const getHealthBadge = (health) => {
    const badges = {
      stressed: { color: 'bg-red-100 text-red-700', label: 'Stressed' },
      moderate: { color: 'bg-yellow-100 text-yellow-700', label: 'Moderate' },
      healthy: { color: 'bg-green-100 text-green-700', label: 'Healthy' },
      vigorous: { color: 'bg-emerald-100 text-emerald-700', label: 'Vigorous' },
    }
    return badges[health] || { color: 'bg-gray-100 text-gray-500', label: 'No Data' }
  }

  const getVegTypeBadge = (type) => {
    const badges = {
      lantana: { color: 'bg-red-50 text-red-700', label: 'Lantana' },
      pasture: { color: 'bg-green-50 text-green-700', label: 'Pasture' },
      savannah_tree: { color: 'bg-amber-50 text-amber-700', label: 'Savannah' },
      other: { color: 'bg-gray-50 text-gray-700', label: 'Other' },
    }
    return badges[type] || { color: 'bg-gray-50 text-gray-700', label: type || 'Unknown' }
  }

  const handleClearClick = (patch) => {
    setClearingModal({ open: true, patch })
    setBiomassWeight('')
    setActualStartDate(patch.actual_start_date || patch.clearing_started || '')
    setActualEndDate(new Date().toISOString().split('T')[0])
  }

  const handleConfirmClear = () => {
    if (clearingModal.patch) {
      clearMutation.mutate({
        patchId: clearingModal.patch.id,
        biomassKg: biomassWeight,
        startDate: actualStartDate,
        endDate: actualEndDate,
      })
    }
  }

  const handleViewClick = (patch) => {
    setViewModal({ open: true, patch })
  }

  const getActionButton = (patch) => {
    if (patch.status === 'cleared') {
      return (
        <button
          onClick={() => handleViewClick(patch)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          <Eye className="h-4 w-4" />
          View
        </button>
      )
    }

    if (patch.status === 'clearing_in_progress') {
      return (
        <button
          onClick={() => handleClearClick(patch)}
          disabled={clearMutation.isPending}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200"
        >
          <CheckCircle className="h-4 w-4" />
          Finish Clearing
        </button>
      )
    }

    if (patch.status === 'scheduled') {
      return (
        <div className="flex gap-2">
          <button
            onClick={() => startClearMutation.mutate(patch.id)}
            disabled={startClearMutation.isPending}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200"
          >
            <Play className="h-4 w-4" />
            Start
          </button>
          <button
            onClick={() => handleClearClick(patch)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200"
          >
            <CheckCircle className="h-4 w-4" />
            Done
          </button>
        </div>
      )
    }

    // Identified - needs scheduling
    return (
      <button
        onClick={() => navigate(`/lantana/${patch.id}/edit`)}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
      >
        <Calendar className="h-4 w-4" />
        Schedule
      </button>
    )
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Error loading patches: {error.message}
      </div>
    )
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Lantana Management"
        subtitle="Track and manage lantana patches for clearing"
        icon={Leaf}
        actions={
          <>
            <Link to="/lantana/field" className="btn bg-indigo-600 text-white hover:bg-indigo-700">
              <Navigation className="h-4 w-4 mr-2" />
              Field View
            </Link>
            <Link to="/lantana/new" className="btn bg-green-600 text-white hover:bg-green-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Patch
            </Link>
          </>
        }
      />

      {/* Tab Switcher */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6">
        <button
          onClick={() => setActiveTab('clearing')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'clearing' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
        >
          <Leaf className="h-4 w-4" />
          Lantana Clearing
        </button>
        <button
          onClick={() => setActiveTab('health')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'health' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
        >
          <Activity className="h-4 w-4" />
          Vegetation Health
        </button>
      </div>

      {activeTab === 'clearing' && (<>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard title="Total Patches" value={stats.total} icon={Leaf} color="primary" />
        <StatCard title="Identified" value={stats.identified} icon={AlertCircle} color="amber" />
        <StatCard title="Scheduled" value={stats.scheduled} icon={Calendar} color="blue" />
        <StatCard title="In Progress" value={stats.inProgress} icon={Play} color="purple" />
        <StatCard title="Cleared" value={stats.cleared} icon={CheckCircle} color="green" />
      </div>

      {/* Workflow Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-blue-900 mb-2">Lantana Clearing Workflow</h3>
        <div className="flex flex-wrap gap-2 text-sm text-blue-800">
          <span className="flex items-center gap-1">
            <AlertCircle className="h-4 w-4" /> Identified
          </span>
          <span>→</span>
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" /> Scheduled
          </span>
          <span>→</span>
          <span className="flex items-center gap-1">
            <Play className="h-4 w-4" /> In Progress
          </span>
          <span>→</span>
          <span className="flex items-center gap-1">
            <CheckCircle className="h-4 w-4" /> Cleared
          </span>
          <span>→</span>
          <span className="flex items-center gap-1">
            <Leaf className="h-4 w-4" /> Biochar
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="section-card p-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {[
            { key: '', label: 'All', count: stats.total, base: 'bg-gray-100 text-gray-700 hover:bg-gray-200', active: 'bg-gray-900 text-white' },
            { key: 'identified', label: 'Identified', count: stats.identified, base: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200', active: 'bg-yellow-600 text-white' },
            { key: 'scheduled', label: 'Scheduled', count: stats.scheduled, base: 'bg-blue-100 text-blue-700 hover:bg-blue-200', active: 'bg-blue-600 text-white' },
            { key: 'clearing_in_progress', label: 'In Progress', count: stats.inProgress, base: 'bg-purple-100 text-purple-700 hover:bg-purple-200', active: 'bg-purple-600 text-white' },
            { key: 'cleared', label: 'Cleared', count: stats.cleared, base: 'bg-green-100 text-green-700 hover:bg-green-200', active: 'bg-green-600 text-white' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === f.key ? f.active : f.base}`}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
      </div>

      {/* Patches List */}
      {isLoading ? (
        <div className="text-center py-12">Loading patches...</div>
      ) : patches?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Leaf className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-4">No lantana patches found</p>
          <Link
            to="/lantana/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus className="h-5 w-5" />
            Add First Patch
          </Link>
        </div>
      ) : (
        <div className="section-card">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-earth-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase">Patch</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase">Paddock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase">Area</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase">Density</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase">Dates</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-earth-600 uppercase">Biomass</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-earth-600 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {patches.map((patch) => {
                const statusBadge = getStatusBadge(patch.status)
                const densityBadge = getDensityBadge(patch.density)
                const StatusIcon = statusBadge.icon
                const isModelDetected = patch.identification_source === 'model'

                return (
                  <tr key={patch.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{patch.patch_code}</span>
                        {isModelDetected && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-violet-100 text-violet-700" title={`Confidence: ${(patch.confidence_score * 100).toFixed(0)}%`}>
                            <Bot className="h-3 w-3" />
                            {patch.confidence_score ? `${(patch.confidence_score * 100).toFixed(0)}%` : 'AI'}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {patch.identified_date
                          ? `Found ${new Date(patch.identified_date).toLocaleDateString()}`
                          : 'Date unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {patch.paddock_name || 'Unassigned'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {patch.area_ha ? `${parseFloat(patch.area_ha).toFixed(2)} ha` :
                       patch.estimated_area_m2 ? `${(parseFloat(patch.estimated_area_m2) / 10000).toFixed(2)} ha` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${densityBadge.color}`}>
                        {densityBadge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                      {patch.target_clear_date && (
                        <div>Target: {new Date(patch.target_clear_date).toLocaleDateString()}</div>
                      )}
                      {patch.actual_start_date && (
                        <div>Started: {new Date(patch.actual_start_date).toLocaleDateString()}</div>
                      )}
                      {patch.actual_end_date && (
                        <div>Done: {new Date(patch.actual_end_date).toLocaleDateString()}</div>
                      )}
                      {!patch.target_clear_date && !patch.actual_start_date && !patch.actual_end_date && '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {patch.estimated_biomass_kg
                        ? `${parseFloat(patch.estimated_biomass_kg).toFixed(0)} kg`
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {getActionButton(patch)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      </>)}

      {/* Vegetation Health Tab */}
      {activeTab === 'health' && (
        <div>
          {/* Health Summary */}
          {healthSnapshot?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard title="Total Patches" value={healthSnapshot.summary.total_patches} icon={Leaf} color="primary" />
              <StatCard title="With Readings" value={healthSnapshot.summary.patches_with_data} icon={BarChart3} color="blue" />
              <StatCard
                title="Avg Vegetation Health"
                value={healthSnapshot.summary.average_ndvi !== null ? healthSnapshot.summary.average_ndvi.toFixed(3) : '-'}
                icon={Activity}
                color="green"
                valueColor={getNdviColor(healthSnapshot.summary.average_ndvi)}
              />
              <div className="card p-5">
                <div className="flex flex-wrap gap-1 mb-3">
                  {Object.entries(healthSnapshot.summary.health_distribution || {}).map(([k, v]) => {
                    const badge = getHealthBadge(k)
                    return (
                      <span key={k} className={`px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                        {badge.label}: {v}
                      </span>
                    )
                  })}
                </div>
                <div className="text-sm text-earth-500 font-medium">Health Distribution</div>
              </div>
            </div>
          )}

          {/* Info box */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-emerald-900 mb-1">Vegetation Health Monitoring</h3>
            <p className="text-sm text-emerald-800">
              Vegetation health scores for all patch types from satellite and drone monitoring.
              Small patches may not have satellite data but can receive high-resolution drone readings.
            </p>
          </div>

          {/* Health Table */}
          {healthLoading ? (
            <div className="text-center py-12">Loading vegetation health data...</div>
          ) : !healthSnapshot?.patches?.length ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <Activity className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No vegetation patches found</p>
            </div>
          ) : (
            <div className="section-card">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-earth-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-600 uppercase">Patch</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-600 uppercase">Paddock</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-600 uppercase">Vegetation</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-600 uppercase">Vigour</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-600 uppercase">Burn Risk</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-600 uppercase">Biomass</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-600 uppercase">Health</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-earth-600 uppercase">Last Reading</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {healthSnapshot.patches.map((p) => {
                    const vegBadge = getVegTypeBadge(p.vegetation_type)
                    const hBadge = getHealthBadge(p.latest_vegetation_health)
                    return (
                      <tr key={p.patch_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {p.patch_code}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${vegBadge.color}`}>
                            {vegBadge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {p.paddock_name || '-'}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm font-mono font-medium ${getNdviColor(p.latest_ndvi)}`}>
                          {p.latest_ndvi !== null ? parseFloat(p.latest_ndvi).toFixed(3) : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                          {p.latest_evi !== null ? parseFloat(p.latest_evi).toFixed(3) : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                          {p.latest_nbr !== null ? parseFloat(p.latest_nbr).toFixed(3) : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {p.latest_biomass_kg_ha !== null ? `${parseFloat(p.latest_biomass_kg_ha).toFixed(0)} kg/ha` : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${hBadge.color}`}>
                            {hBadge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                          {p.latest_observation_date
                            ? new Date(p.latest_observation_date).toLocaleDateString()
                            : 'Never'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Clear Patch Modal (with flexible dates + biomass) */}
      {clearingModal.open && clearingModal.patch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Mark Patch as Cleared
            </h3>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Patch:</strong> {clearingModal.patch.patch_code}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Paddock:</strong> {clearingModal.patch.paddock_name || 'Unknown'}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Area:</strong> {clearingModal.patch.area_ha ? `${clearingModal.patch.area_ha} ha` : 'Unknown'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={actualStartDate}
                  onChange={(e) => setActualStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date *
                </label>
                <input
                  type="date"
                  value={actualEndDate}
                  onChange={(e) => setActualEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Scale className="h-4 w-4 inline mr-1" />
                Biomass Weight (kg) *
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={biomassWeight}
                onChange={(e) => setBiomassWeight(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Enter weight of cleared biomass"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Weigh the cleared lantana biomass before it's processed into biochar
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setClearingModal({ open: false, patch: null })
                  setBiomassWeight('')
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmClear}
                disabled={!biomassWeight || !actualEndDate || clearMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle className="h-4 w-4" />
                {clearMutation.isPending ? 'Saving...' : 'Mark as Cleared'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Cleared Patch Modal (read-only) */}
      {viewModal.open && viewModal.patch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Patch Details
              </h3>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3" />
                Cleared
              </span>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Patch Code</p>
                  <p className="font-medium">{viewModal.patch.patch_code}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Paddock</p>
                  <p className="font-medium">{viewModal.patch.paddock_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Area</p>
                  <p className="font-medium">{viewModal.patch.area_ha ? `${viewModal.patch.area_ha} ha` : 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Density</p>
                  <p className="font-medium capitalize">{viewModal.patch.density || 'Unknown'}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div>
                  <p className="text-xs text-gray-500">Identified</p>
                  <p className="font-medium text-sm">
                    {viewModal.patch.identified_date
                      ? new Date(viewModal.patch.identified_date).toLocaleDateString()
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Started</p>
                  <p className="font-medium text-sm">
                    {viewModal.patch.actual_start_date
                      ? new Date(viewModal.patch.actual_start_date).toLocaleDateString()
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="font-medium text-sm">
                    {viewModal.patch.actual_end_date
                      ? new Date(viewModal.patch.actual_end_date).toLocaleDateString()
                      : viewModal.patch.cleared_date
                      ? new Date(viewModal.patch.cleared_date).toLocaleDateString()
                      : '-'}
                  </p>
                </div>
              </div>

              {/* Biomass Info */}
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-2">
                  <Scale className="h-4 w-4 text-green-600" />
                  <p className="text-sm font-medium text-gray-900">Biomass</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">
                    {viewModal.patch.estimated_biomass_kg
                      ? `${parseFloat(viewModal.patch.estimated_biomass_kg).toFixed(0)} kg`
                      : 'Not recorded'}
                  </p>
                </div>
              </div>

              {/* Biochar Status */}
              <div className="pt-4 border-t">
                <p className="text-xs text-gray-500 mb-2">Biochar Status</p>
                {viewModal.patch.biochar_batch_id ? (
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm text-orange-800">
                      Linked to biochar batch
                    </p>
                  </div>
                ) : (
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      Awaiting biochar processing
                    </p>
                    <p className="text-xs text-yellow-600 mt-1">
                      Create a biochar batch to process this biomass
                    </p>
                  </div>
                )}
              </div>

              {viewModal.patch.location_description && (
                <div className="pt-4 border-t">
                  <p className="text-xs text-gray-500">Location Notes</p>
                  <p className="text-sm text-gray-700">{viewModal.patch.location_description}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setViewModal({ open: false, patch: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
